import { Component, OnDestroy, OnInit, ChangeDetectionStrategy, inject } from '@angular/core';
import { ActivatedRoute, ActivatedRouteSnapshot, Router } from '@angular/router';
import { Location } from '@angular/common';
import { Logger, LoggerFactory } from '../core/logger.service';
import { AccountTypeCustomer } from 'app/back-office/account.model';
import { SubscriptionService } from 'app/subscription/subscription.service';
import { SubscriptionDetails } from 'app/subscription/subscription.model';
import { CurrentContextService } from 'app/core/current-context.service';
import { IdentityService } from 'app/core/identity/identity.service';
import { SiteContextService } from 'app/core/site-context.service';
import { ExpiredAccountAccessService } from 'app/core/authorization/expired-account-access.service';
import { Subscription } from 'rxjs';

@Component({
  selector: 'app-account-expired',
  templateUrl: './account-expired.component.html',
  styleUrls: ['account-expired.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class AccountExpiredComponent implements OnInit, OnDestroy {
  private logger: Logger;
  private subscriptionArray: Subscription[] = [];
  public isSelfServiceCustomer: boolean;
  public isSelfServiceTrial: boolean;
  public loading = true;
  public canViewAccountAnyway = false;
  public isPaused = false;

  private readonly identityService = inject(IdentityService);
  private readonly siteContext = inject(SiteContextService);
  private readonly expiredAccountAccess = inject(ExpiredAccountAccessService);
  private readonly originalShouldReuseRoute: (
    future: ActivatedRouteSnapshot,
    current: ActivatedRouteSnapshot,
  ) => boolean;
  private expiredSiteId: number | undefined;
  private attemptedUrl = '/';

  constructor(
    loggerFactory: LoggerFactory,
    private activatedRoute: ActivatedRoute,
    private location: Location,
    private router: Router,
    private readonly currentContextService: CurrentContextService,
    private readonly subscriptionService: SubscriptionService,
  ) {
    this.logger = loggerFactory.get('AccountExpiredComponent');
    // override the route reuse strategy
    this.originalShouldReuseRoute = this.router.routeReuseStrategy.shouldReuseRoute;
    this.router.routeReuseStrategy.shouldReuseRoute = function () {
      return false;
    };
  }

  async ngOnInit() {
    this.subscriptionArray.push(
      this.currentContextService.currentContextBS.subscribe((currentContext) => {
        if (currentContext) {
          this.isSelfServiceCustomer = currentContext.customer.type === AccountTypeCustomer.SelfService;
          if (this.isSelfServiceCustomer) {
            this.subscriptionService.getSubscription(currentContext.customer.id).subscribe(
              (subscription: SubscriptionDetails) => {
                if (!subscription) {
                  this.isSelfServiceTrial = true;
                }
              },
              (error) => {
                this.logger.error(error);
              },
            );
          }
          this.loading = false;
        }
      }),
    );

    const replaceUrl = this.activatedRoute.snapshot.queryParams['url'];
    if (replaceUrl && replaceUrl !== 'account-expired') {
      this.location.replaceState(replaceUrl);
      this.attemptedUrl = replaceUrl;
    }

    const currentSite = await this.siteContext.getCurrentSiteAsync();
    this.expiredSiteId = currentSite?.id;
    this.isPaused = currentSite?.isPaused === true && !currentSite.isExpired;
    this.canViewAccountAnyway =
      this.identityService.currentIdentity?.isAdmin() === true && this.expiredSiteId !== undefined;
  }

  public openPlanAndBilling() {
    this.router.navigate(['/settings/plan-billing']);
  }

  public viewAccountAnyway() {
    if (this.expiredSiteId === undefined) {
      return;
    }
    this.expiredAccountAccess.unlock(this.expiredSiteId);
    // ngOnInit already put attemptedUrl in the address bar while the router stayed on
    // account-expired, so replaceUrl avoids a second history entry for the same URL.
    this.router.navigateByUrl(this.attemptedUrl, { replaceUrl: true });
  }

  ngOnDestroy(): void {
    // The constructor disables route reuse app-wide. Unlocked admins leave this page and keep
    // browsing, so it has to go back or every other route loses reuse for the whole session.
    this.router.routeReuseStrategy.shouldReuseRoute = this.originalShouldReuseRoute;
    this.subscriptionArray.forEach((subs) => subs && subs.unsubscribe());
  }
}
